import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Switch,
  Avatar,
  Tooltip,
  makeStyles,
} from "@fluentui/react-components";

import {
  ArrowClockwiseRegular,
  HomeRegular,
  BotRegular,
  BotSparkleRegular,
  ChevronDownRegular,
  ChatHelpRegular,
} from "@fluentui/react-icons";
import { getAccountData } from "../taskpane";
import config from "../../../config/config";

/**
 * Interface définissant les propriétés du composant FullMenu.
 */
interface FullMenuProps {
  isDarkMode: boolean; // Indique si le mode sombre est activé
  handleDarkModeChange: () => void; // Fonction pour basculer entre mode sombre et clair
  setCurrentView: (view: string) => void; // Fonction pour changer la vue actuelle
  logoutFunc: () => void; // Fonction pour déconnecter l'utilisateur
  isLoggedIn: boolean; // Indique si l'utilisateur est connecté
  reload: boolean; // Indique si les données doivent être rechargées
}

/**
 * Fonction utilitaire pour actualiser les crédits restants de l'utilisateur.
 * @param {React.Dispatch<React.SetStateAction<string>>} setRequestsLeft - Fonction pour mettre à jour les crédits restants.
 */
export const refreshRequestsLeft = async (
  setRequestsLeft: React.Dispatch<React.SetStateAction<string>>
): Promise<void> => {
  try {
    const accountData = await getAccountData();
    setRequestsLeft(accountData.requests_left.toString());
  } catch (error) {
    console.error("Échec de l'actualisation des requêtes restantes :", error);
  }
};

/**
 * Liste des fonctionnalités disponibles dans le menu.
 */
const MENU_ITEMS = [
  { label: "Assistant général", view: "GeneralAssistant" },
  { label: "Extraction d'informations", view: "ExtractionPage" },
  { label: "Uniformiser des données", view: "StandardisationPage" },
  { label: "Génération de formules", view: "ChatPage" },
  { label: "Guide du manuel", view: "GuideLivre" },
] as const;

/**
 * Composant FullMenu : gère l'affichage du menu principal, des fonctionnalités, et des options utilisateur.
 * @param {FullMenuProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const FullMenu: React.FC<FullMenuProps> = ({
  isDarkMode,
  handleDarkModeChange,
  setCurrentView,
  logoutFunc,
  reload,
}) => {
  const styles = useStyles();

  // États locaux pour gérer l'affichage et les données utilisateur
  const [sidebarOpen, setSidebarOpen] = useState(false); // Indique si la barre latérale est ouverte
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false); // Indique si le menu des fonctionnalités est ouvert
  const [username, setUsername] = useState(""); // Nom d'utilisateur
  const [requestsLeft, setRequestsLeft] = useState(""); // Nombre de requêtes restantes
  const [avatar, setAvatar] = useState(""); // Avatar de l'utilisateur
  const [isHovered, setIsHovered] = useState(false); // Indique si le bouton de rechargement est survolé
  const creditsIcon = useMemo(() => "assets/xcredits_icon.png", []); // Icône des crédits

  const sidebarStyle = {
    backgroundColor: isDarkMode ? "#141414" : "#ffffff",
  };

  /**
   * Bascule l'affichage du menu des fonctionnalités.
   */
  const toggleFeatures = () => setIsFeaturesOpen(!isFeaturesOpen);

  /**
   * Récupère les données utilisateur (nom, crédits, avatar).
   */
  const fetchUserData = async () => {
    try {
      const user = await getAccountData();
      setUsername(user.username);
      setRequestsLeft(user.requests_left.toString());
      setAvatar(user.avatar.toString());
    } catch (error) {
      console.error("Erreur lors de la récupération des données utilisateur :", error);
    }
  };

  // Charge les données utilisateur au montage du composant
  useEffect(() => {
    fetchUserData();
  }, []);

  // Recharge les données utilisateur si nécessaire
  useEffect(() => {
    if (reload) {
      fetchUserData();
    }
  }, [reload]);

  const refreshTimeout = useRef<NodeJS.Timeout | null>(null);

  /**
   * Actualise les crédits restants de l'utilisateur.
   */
  const handleRefreshRequests = () => {
    if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    refreshTimeout.current = setTimeout(async () => {
      await refreshRequestsLeft(setRequestsLeft);
    }, 300);
  };

  // Rendu du composant
  return (
    <div className={styles.menuContainer}>
      {/* Menu Bouton Sidebar */}
      <Button className={styles.menuButton} onClick={() => setSidebarOpen(true)}>
        ☰
      </Button>
      {sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)}></div>}

      {/* Sidebar */}
      <div
        className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ""}`}
        style={sidebarStyle}
      >
        <div className={styles.sidebarHeader}>Menu</div>
        <Button className={styles.closeButton} onClick={() => setSidebarOpen(false)}>
          ✖
        </Button>
        <div className={styles.menuItemsContainer}>
          <Button
            className={styles.menuItem}
            onClick={() => {
              setCurrentView("Home");
              setSidebarOpen(false);
            }}
          >
            <HomeRegular className={styles.menuIcon} />
            <p className={styles.featuresLabel}>Accueil</p>
          </Button>
          <div className={styles.menuItemWrapper}>
            <Button className={styles.menuDropdownParent} onClick={toggleFeatures}>
              <BotRegular className={styles.menuIcon} />
              <p className={styles.featuresLabel}>Fonctionnalités</p>
              <ChevronDownRegular className={styles.dropdownArrow} />
            </Button>

            {/* Dropdown fonctionnalités */}
            {isFeaturesOpen && (
              <div
                className={`${styles.featuresDropdown} ${isFeaturesOpen ? styles.featuresDropdownOpen : ""}`}
                style={sidebarStyle}
              >
                {MENU_ITEMS.map(({ label, view }) => (
                  <Button
                    key={view}
                    className={styles.featuresItem}
                    onClick={() => {
                      setCurrentView(view);
                      setSidebarOpen(false);
                    }}
                  >
                    <BotSparkleRegular className={styles.menuIcon} />
                    <p className={styles.featuresLabel}>{label}</p>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.helpButtonContainer}>
          <Button
            className={styles.menuItem}
            onClick={() => {
              window.open(config.helpUrl, "_blank");
            }}
          >
            <ChatHelpRegular className={styles.menuIcon} />
            <p className={styles.featuresLabel}>Help</p>
          </Button>
        </div>
      </div>

      {/* Menu à droite (Crédits, rechargement, profil) */}
      <div className={styles.menuRight}>
        <span className={styles.requestsLeft}>{requestsLeft}</span>

        {/* Crédits et bouton de rechargement */}
        <Tooltip relationship="label" content="Actualiser les crédits">
          <Button
            className={styles.refreshButton}
            onClick={handleRefreshRequests}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {isHovered ? (
              <ArrowClockwiseRegular className={styles.refreshIcon} />
            ) : (
              <Avatar image={{ src: creditsIcon }} className={styles.creditsIcon} />
            )}
          </Button>
        </Tooltip>

        {/* Menu du profil */}
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Avatar className={styles.userAvatar} name={username} />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem onClick={() => setCurrentView("CreditPage")}>Crédits</MenuItem>
              <MenuItem onClick={() => setCurrentView("Profil")}>Profil</MenuItem>
              <MenuItem onClick={logoutFunc}>Se déconnecter</MenuItem>
              <MenuItem onClick={handleDarkModeChange}>
                <div className={styles.darkModeToggle}>
                  <span>Mode Sombre</span>
                  <Switch checked={isDarkMode} aria-label="Basculer en mode sombre" />
                </div>
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </div>
  );
};

export default FullMenu;

const useStyles = makeStyles({
  /* Top menu */
  menuContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "0px 20px 10px 20px",
    paddingTop: "10px",
  },
  menuRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  requestsLeft: {
    fontWeight: "bold",
    fontSize: "14px",
  },
  refreshButton: {
    borderRadius: "50%",
    padding: "0",
    fontSize: "16px",
    height: "32px",
    width: "32px",
    minWidth: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  refreshIcon: {
    fontSize: "18px",
  },
  creditsIcon: {
    width: "40px",
    height: "40px",
  },
  userAvatar: {
    cursor: "pointer",
    backgroundColor: "#1AA05D",
  },
  darkModeToggle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  menuButton: {
    minWidth: "0px",
  },

  /* Sidebar */
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 50,
  },
  sidebar: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "250px",
    height: "100%",
    backgroundColor: "#ffffff",
    boxShadow: "2px 0px 15px rgba(0, 0, 0, 0.2)",
    transform: "translateX(-100%)",
    transition: "transform 0.3s ease-in-out",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    zIndex: 100,
  },
  sidebarOpen: {
    transform: "translateX(0)",
  },
  sidebarHeader: {
    fontSize: "18px",
    fontWeight: "bold",
    padding: "20px 0 20px 20px",
  },
  closeButton: {
    minWidth: "0px",
    position: "absolute",
    top: "10px",
    right: "10px",
  },
  menuItemsContainer: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: "5px",
  },
  menuItemWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    position: "relative",
    width: "100%",
  },
  menuItem: {
    display: "flex",
    justifyContent: "left",
    borderRadius: "10px",
    height: "40px",
    margin: "0px 10px",
  },
  menuIcon: {
    flexShrink: 0,
    fontSize: "20px",
  },
  menuDropdownParent: {
    display: "flex",
    justifyContent: "left",
    borderRadius: "10px",
    height: "40px",
    margin: "0px 10px 5px 10px",
    width: "230px",
  },
  dropdownArrow: {
    marginLeft: "auto",
  },
  featuresDropdown: {
    position: "absolute",
    top: "100%",
    right: "10px",
    width: "80%",
    maxWidth: "300px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    overflow: "hidden",
    opacity: 0,
    zIndex: 50,
  },

  featuresDropdownOpen: {
    opacity: 1,
    transform: "translateY(0)",
  },

  featuresItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    margin: "0 0 5px 0",
    width: "200px",
    borderRadius: "10px",
    minHeight: "40px",
    height: "auto",
    padding: "10px 10px",
    gap: "10px",
    position: "relative",
  },
  featuresLabel: {
    flexGrow: 1,
    textAlign: "center",
    margin: 0,
  },
  helpButtonContainer: {
    margin: "20px 10px",
    alignSelf: "flex-end",
  },
});
